<template>
  <div>
    <div
      :id="id"
      class="rs-draggable"
    >
      <div
        v-show="disableDrag"
        :id="`${id}_placeholder`"
        :class="`rs-item-placeholder ${gridClass}`"
      >
        <slot />
      </div>
      <div
        v-if="Muuri"
        v-show="!disableDrag"
        :id="`${id}_grid`"
        class="rs-draggable-grid"
        :class="gridClass"
      />
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'RsDraggable',

  props: {
    id: {
      type: String,
      required: true,
    },

    gridClass: {
      type: String,
      default: '',
    },

    disableDrag: {
      type: Boolean,
      default: false,
    },

    dragHandle: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      Muuri: null as any,
      grid: null as any,
      oldDataValues: [] as any[],
      placeholderDiv: null as Element | null,
      draggableDiv: null as Element | null,
      options: {
        dragEnabled: true,
        itemPlaceholderClass: 'bg-grey',
        dragHandle: this.dragHandle,
      },
    };
  },

  // Watch our <slot></slot> for changes and re-init drag if items change.
  watch: {
    placeholderDiv() {
      this.initDrag();
    },

    disableDrag() {
      if (!this.disableDrag) {
        this.$nextTick(() => {
          this.initDrag();
        });
      } else {
        this.$emit('on-drag-ready');
      }
    },
  },

  mounted() {
    if (window) {
      import('muuri').then((module) => {
        this.Muuri = module.default;

        this.$nextTick(() => {
          this.placeholderDiv = document.querySelector(`#${this.id}_placeholder`);
          this.draggableDiv = document.querySelector(`#${this.id}_grid`);
          this.initDrag();
        });
      });
    }
  },

  beforeDestroy() {
    this.destroyDrag();
  },

  methods: {
    handleDrag(item: any) {
      const items = this.grid.getItems();

      const data = Array.from(items, (itm: any) => {
        const el = itm.getElement();
        return (el as any).dragValue;
      });

      // Validates that a change did in fact occur.
      const changeArrayCount = this.oldDataValues.filter((v: any, idx: number) => v === data[idx]);

      // If change array count is not equal to our old data, emit a change event.
      if (changeArrayCount.length !== this.oldDataValues.length) {
        this.oldDataValues = data;
        this.$emit('change', data.filter((d: any) => d));
      }
    },

    destroyDrag() {
      if (this.grid) {
        this.grid.destroy(true);
        this.grid = null;
      }

      this.oldDataValues = [];
    },

    initDrag(): void {
      setTimeout(() => {
        this.$emit('on-drag-init');

        if (window) {
          this.destroyDrag();

          const vals = this.placeholderDiv?.children ? Array.from(this.placeholderDiv?.children) : [];

          if (vals.length > 0) {
            vals.forEach((item: Element) => {
              const clone = item.cloneNode(true);

              const innerWrapper = document.createElement('div');
              innerWrapper.className = 'rs-item-content';
              innerWrapper.append(clone);

              const outerWrapper = document.createElement('div');
              outerWrapper.className = `rs-item ${((item as any).dragClass || '')}`;
              (outerWrapper as any).dragValue = (item as any).dragValue;
              outerWrapper.append(innerWrapper);

              if (this.draggableDiv) {
                this.draggableDiv.append(outerWrapper);
              }

              this.oldDataValues.push((item as any).dragValue);
            });

            this.grid = new this.Muuri(`#${this.id}_grid`, this.options);
            this.grid.on('dragEnd', this.handleDrag);
            this.grid.on('layoutStart', (items: any[], isInstant: boolean) => setTimeout(() => this.$emit('on-drag-ready'), 300));
          } else {
            this.$emit('on-drag-ready');
          }
        } else {
          this.$emit('on-drag-error');
          // eslint-disable-next-line no-console
          console.warn('JS window object is undefined. Drag cannot load');
        }
      }, 0);
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.rs-draggable {
  .rs-draggable-grid {
    position: relative !important;
  }

  .rs-item {
    display: block;
    position: absolute !important;
    cursor: grabbing;

    &.muuri-item-dragging {
      cursor: grabbing;
      z-index: 3;
    }

    &.muuri-item-releasing {
      cursor: grabbing;
      z-index: 2;
    }

    &.muuri-item-hidden {
      cursor: grabbing;
      z-index: 0;
    }

    .rs-item-content {
      cursor: grabbing;
      position: relative;
      width: 100%;
      height: 100%;
    }

    .rs-item-content > * {
      z-index: -1;
    }
  }
}
</style>
